import React, { useState } from 'react';
import DateTimePicker from '../../../components/DateTimePicker';
import { TanTable } from '../../../components/TanTable';
import service, {
  type CreditMemoReportResponse, type CreditMemoReportRow,
} from '../../../services/accountingReports.service';
import { formatCurrency } from '../../../utils/money';
import { creditMemoColumns } from './creditMemoColumns';
import { calendarDate, dateParam, reportError, REPORT_BUTTON } from './reportUtils';

/**
 * Credit Memo Report — reports_cms.pl::creditmemoreport (:4712).
 *
 * Every rebill/credit LINE whose `rebilldate` falls in the range, one row per line: a
 * record with three GL codes is three rows, and that is what makes the amounts add up.
 * V1 delivers this as an .xls download; a grid is the 360 equivalent and it filters,
 * sorts, groups and exports on its own.
 *
 * Both dates are required, as in V1 — there is no "all time" mode, and `rebillinfo` has
 * 17,736 live records.
 */
export default function CreditMemoReport() {
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [result, setResult] = useState<CreditMemoReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    // The same two rules the backend enforces, checked here so the message appears next
    // to the controls the user has to fix rather than after a round trip.
    if (!start || !end) { setError('Enter a From and a Thru date.'); return; }
    if (start > end) { setError('End date must be on or after start date.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      setResult(await service.getCreditMemoReport({
        start: dateParam(start), end: dateParam(end),
      }));
    } catch (e) {
      setError(reportError(e, 'Unable to load the Credit Memo Report. Please try again.'));
    } finally { setLoading(false); }
  };

  return <div className="p-4 space-y-4 text-label">
    <h1 className="text-xl font-semibold">Credit Memo Report</h1>
    <div className="rounded-lg border border-theme-border-primary bg-theme-bg-primary p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
        <DateTimePicker mode="date" labelType="custom" label="From Date" ariaLabel="From Date"
          value={start} onChange={v => setStart(calendarDate(v))} disabled={loading} />
        <DateTimePicker mode="date" labelType="custom" label="Thru" ariaLabel="Thru"
          value={end} onChange={v => setEnd(calendarDate(v))} disabled={loading} />
      </div>
      <p className="text-sm text-muted">
        One row per credit/rebill line item, by Rebill Date. Zero-value lines and storage
        credits are excluded, matching what the Sage export picks up.
      </p>
      <button className={REPORT_BUTTON} onClick={run} disabled={loading}>
        {loading ? 'Running…' : 'Run Report'}
      </button>
      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
    </div>

    {result && <div className="space-y-2">
      <div className="text-sm text-muted">
        {result.rows.length.toLocaleString()} line{result.rows.length === 1 ? '' : 's'}
        {' · '}
        <span className="font-semibold text-label">{formatCurrency(result.total)}</span>
        {' credited · '}{result.criteria}
        {result.truncated && (
          <span className="text-amber-600 dark:text-amber-400">
            {' '}— capped at 50,000 lines, and the total covers only what is shown.
            Narrow the dates to see the rest.
          </span>
        )}
      </div>
      <TanTable<CreditMemoReportRow> settingsKey="accounting-credit-memo-report"
        cardTitle="Credit Memo Report" data={result.rows} columns={creditMemoColumns}
        loading={loading} fillContainer enableColumnFilters />
    </div>}
  </div>;
}
